import React, { useEffect, useState, useCallback, useRef } from 'react';
import Wrapper from '../../components/Wrapper';
import Api from '../../Api';
import SpyAdminCard from '../../components/new_component/SpyAdminCard';
import SipDragDropZones from '../../components/new_component/SipDragDropZones';
import { shortFunction } from '../../translate';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, CircularProgress, IconButton, Alert, Divider } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import InfoIcon from '@mui/icons-material/Info';
import { toast } from 'react-toastify';

const { REACT_APP_SERVER } = process.env;
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a';
const getItemId = (item) => item.buddy || String(item.id);

export default function ShortFunctionSpy() {
  const langIndex = +localStorage.getItem('atsLang') || 1;
  const [allSips, setAllSips] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [activeAdmins, setActiveAdmins] = useState([]);
  const [adminChildrenMap, setAdminChildrenMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [helpAnchor, setHelpAnchor] = useState(null);
  const helpCloseTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const handleHelpOpen = (e) => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current);
    setHelpAnchor(e.currentTarget);
  };
  const handleHelpClose = () => {
    helpCloseTimeoutRef.current = setTimeout(() => setHelpAnchor(null), 150);
  };
  const handleHelpPopperEnter = () => {
    if (helpCloseTimeoutRef.current) clearTimeout(helpCloseTimeoutRef.current);
  };
  const handleHelpPopperLeave = () => {
    helpCloseTimeoutRef.current = setTimeout(() => setHelpAnchor(null), 150);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [sipRes, adminsRes] = await Promise.all([
        Api.spySip(),
        Api.apyAdminGet(),
      ]);
      const nonAdmin = sipRes?.data?.sip || [];
      const admins = adminsRes?.data?.sip || [];
      const adminBuddies = admins.map((a) => a.buddy);

      const byBuddy = new Map();
      nonAdmin.forEach((s) => byBuddy.set(s.buddy, { ...s }));
      admins.forEach((s) => byBuddy.set(s.buddy, { ...s }));

      setAllSips(Array.from(byBuddy.values()));
      setSelectedIds(adminBuddies);
      setActiveAdmins(admins);

      const childMap = {};
      const childRes = await Promise.all(admins.map((a) => Api.apyOperatorGet(a.buddy)));
      admins.forEach((a, i) => {
        const list = childRes[i]?.data?.sip || [];
        childMap[a.buddy] = list.map((o) => o.spy || o.sip?.buddy).filter(Boolean);
      });
      setAdminChildrenMap(childMap);
    } catch (e) {
      console.error(e);
      setAllSips([]);
      setSelectedIds([]);
      setActiveAdmins([]);
      setAdminChildrenMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectionChange = useCallback(
    (updater) => {
      setSelectedIds((prev) => {
        const next = updater(prev);
        const added = next.filter((b) => !prev.includes(b));
        const removed = prev.filter((b) => !next.includes(b));
        (async () => {
          try {
            for (const buddy of added) await Api.spyAdminSet(buddy, 1);
            for (const buddy of removed) await Api.spyAdminSet(buddy, 0);
            await fetchData();
          } catch (e) {
            toast.error('Failed to update spy admin');
            fetchData();
          }
        })();
        return next;
      });
    },
    [fetchData]
  );

  return (
    <Wrapper>
      <div className="tableParent">
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5 }}>
            <IconButton
              size="small"
              sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
              onClick={() => navigate('/short_function')}
              aria-label="Back"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
            </IconButton>
            <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
              {shortFunction.spy?.[langIndex] || 'Supervisor'}
            </Typography>
            <IconButton
              size="small"
              onMouseEnter={handleHelpOpen}
              onMouseLeave={handleHelpClose}
              aria-label={shortFunction.operator_help_tooltip?.[langIndex] || 'How it works'}
              sx={{ color, '&:hover': { backgroundColor: `${color}15` } }}
            >
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Box>

          <Alert
            icon={<InfoIcon />}
            severity="success"
            sx={
              REACT_APP_SERVER !== 'ATS'
                ? { mb: 2 }
                : {
                    mb: 2,
                    border: `1px solid ${color}`,
                    backgroundColor: `${color}10`,
                    '& .MuiAlert-icon': { color },
                  }
            }
          >
            <Typography variant="body2">
              {shortFunction.spy_desc?.[langIndex] ||
                'Supervisor mode: assign supervisors and operators, then dial the code and make the call. *1* = Supervisor only listens (for a specific employee use *1*29 where 29 is employee private number). *2* = Supervisor and employee can hear each other while listening. *3* = Client can also hear the Supervisor.'}
            </Typography>
          </Alert>

          <Divider />

          <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, minHeight: 160 }}>
            <SipDragDropZones
              items={allSips}
              selectedIds={selectedIds}
              onSelectionChange={handleSelectionChange}
              zoneNotSelectedTitle={shortFunction.spy_not_selected?.[langIndex] || 'Not spy admin'}
              zoneSelectedTitle={shortFunction.spy_selected?.[langIndex] || 'Spy admin'}
              getItemId={getItemId}
              loading={loading}
              emptyMessage="No SIPs"
              helpAnchorEl={helpAnchor}
              onHelpPopperEnter={handleHelpPopperEnter}
              onHelpPopperLeave={handleHelpPopperLeave}
              helpTitle={shortFunction.operator_help_tooltip?.[langIndex] || 'How it works'}
              helpDescription={shortFunction.spy_help_drag?.[langIndex] || 'Drag SIPs to the right to make them spy admins. Below are the active spy admins.'}
              helpDragMeText={shortFunction.operator_help_drag_me?.[langIndex] || 'SIP — drag me'}
              searchPlaceholder="Search SIP…"
            />
          </Paper>

          <Divider />

          <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
              {shortFunction.spy_active_cards_title?.[langIndex] || 'Active spy admins'}
            </Typography>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress size={28} sx={{ color }} />
              </Box>
            ) : activeAdmins.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No active spy admins. Drag SIPs to the right column above to add them.
              </Typography>
            ) : (
              <Box className="d-flex flex-wrap" sx={{ gap: 1.5 }}>
                {activeAdmins.map((item, index) => (
                  <SpyAdminCard
                    key={item.buddy || index}
                    item={item}
                    allSips={allSips}
                    adminChildrenMap={adminChildrenMap}
                    onSaved={fetchData}
                  />
                ))}
              </Box>
            )}
          </Paper>
        </Paper>
      </div>
    </Wrapper>
  );
}
